import { useState, useEffect } from 'react';

/**
 * Custom hook to track online/offline status
 * @returns boolean indicating if the browser is online
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Dispatch custom event for other components to listen to
      window.dispatchEvent(new CustomEvent('network-status-change', {
        detail: { isOnline: true }
      }));
    };

    const handleOffline = () => {
      setIsOnline(false);
      // Dispatch custom event for other components to listen to
      window.dispatchEvent(new CustomEvent('network-status-change', {
        detail: { isOnline: false }
      }));
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}

/**
 * Hook that provides online status and connection quality
 * @returns object with isOnline status and effective connection type
 */
export function useNetworkStatus() {
  const isOnline = useOnlineStatus();
  const [effectiveType, setEffectiveType] = useState<string>('unknown');
  const [downlink, setDownlink] = useState<number | undefined>(undefined);

  useEffect(() => {
    // Check if Network Information API is available
    const connection = (navigator as any).connection ||
                      (navigator as any).mozConnection ||
                      (navigator as any).webkitConnection;

    if (connection) {
      const updateConnectionStatus = () => {
        setEffectiveType(connection.effectiveType || 'unknown');
        setDownlink(connection.downlink);
      };

      updateConnectionStatus();
      connection.addEventListener('change', updateConnectionStatus);

      return () => {
        connection.removeEventListener('change', updateConnectionStatus);
      };
    }
  }, []);

  return {
    isOnline,
    effectiveType,
    downlink,
    isSlowConnection: effectiveType === 'slow-2g' || effectiveType === '2g'
  };
}
